'use client';

import { useState } from 'react';
import { useWishlist } from '@/context/wishlist-context';
import { Product } from '@/lib/shopify/types';

interface WishlistButtonProps {
  product: Product;
  variant?: 'icon' | 'full';
  className?: string;
}

export default function WishlistButton({ product, variant = 'icon', className = '' }: WishlistButtonProps) {
  const { isInWishlist, toggleItem } = useWishlist();
  const [isAnimating, setIsAnimating] = useState(false);

  const inWishlist = isInWishlist(product.id);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setIsAnimating(true);
    toggleItem(product);

    setTimeout(() => setIsAnimating(false), 300);
  };

  if (variant === 'full') {
    return (
      <button
        onClick={handleClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          width: '100%',
          padding: '12px 16px',
          backgroundColor: inWishlist ? 'var(--brand-blue)' : '#ffffff',
          color: inWishlist ? '#ffffff' : 'var(--brand-blue)',
          fontSize: '14px',
          fontWeight: '600',
          border: '1px solid var(--brand-blue)',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          boxSizing: 'border-box'
        }}
        className={className}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill={inWishlist ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth={inWishlist ? 0 : 1.5}
          style={{
            width: '20px',
            height: '20px',
            transition: 'transform 0.2s ease',
            transform: isAnimating ? 'scale(1.2)' : 'scale(1)'
          }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
        </svg>
        {inWishlist ? 'Added to Wishlist' : 'Add to Wishlist'}
      </button>
    );
  }

  // Icon variant (default)
  return (
    <button
      onClick={handleClick}
      aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '40px',
        height: '40px',
        backgroundColor: '#ffffff',
        border: 'none',
        borderRadius: '50%',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}
      className={className}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill={inWishlist ? '#e11d48' : 'none'}
        stroke={inWishlist ? '#e11d48' : '#000000'}
        strokeWidth={inWishlist ? 0 : 1.5}
        style={{
          width: '20px',
          height: '20px',
          transition: 'transform 0.2s ease',
          transform: isAnimating ? 'scale(1.3)' : 'scale(1)'
        }}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
      </svg>
    </button>
  );
}
